/**
 * Pure-logic helpers for PartyOrbs — extracted into a Phaser-free module
 * so the unit tests can import them under Node (vitest default env;
 * Phaser pulls in `window` at module init).
 *
 * Constants are the single source of truth (eng review Issue 5) and the
 * downstream PartyOrbs.ts + gestureRouter.ts (step 5) both import from
 * here.
 */
import type { PartyMember } from '../state/runState';

export const ORB_BENCH_SIZE = 52;
export const ORB_LEAD_SIZE = 60;
/** Sprite sizes leave a 4 px clearance per side inside the orb so
 *  Bulbasaur's bulb / Charmander's tail / Pikachu's ears don't paint
 *  over the 2 px HP ring border. (Originally 48 / 56 — the BW
 *  animated GIFs draw to their native sprite bounds and the limbs
 *  visibly overlapped the ring on real-device renders.) */
export const ORB_SPRITE_BENCH = 44;
export const ORB_SPRITE_LEAD = 52;
export const ORB_GAP = 8;
/** Lead orb sits this many pixels HIGHER than the bench baseline.
 *  iOS dock active-app convention — the elevated icon reads as "active". */
export const ORB_LEAD_LIFT = 2;
/** Horizontal pitch between bench orbs. Used by drag math in step 5b. */
export const SLOT_PITCH = ORB_BENCH_SIZE + ORB_GAP;
export const STRIP_BOTTOM_INSET = 16;

/** Future Cartridge gameplay-semantic state colors — match tokens.css
 *  --ok-green / --warn-amber / --err-red. Live in the same module as
 *  the orb layout consts so both the orb ring and (eventually) any
 *  pure-logic test for HP visuals can read from a single source.
 *  Replaces the Kanto-at-Golden-Hour olive/peach/coral ramp. */
export const OK_GREEN = '#2EBD68';
export const WARN_AMBER = '#FFB938';
export const ERR_RED = '#FF453A';

/** HP ring color via the threshold matrix BattleScene's HP bar uses
 *  for its fill — keeps cross-surface signal consistent.
 *  ok-green >50% / warn-amber 25-50% / err-red ≤25%.
 *  Defensive: max ≤ 0 falls through to err-red (treats "no data" as
 *  "critically low" — safer than rendering a healthy ring on a dead
 *  Pokémon during a save-restore race). */
export function hpRingColor(current: number, max: number): string {
  if (max <= 0) return ERR_RED;
  const frac = current / max;
  if (frac > 0.5) return OK_GREEN;
  if (frac > 0.25) return WARN_AMBER;
  return ERR_RED;
}

export interface SlotLayout {
  left: number;
  top: number;
  size: number;
  spriteSize: number;
  isLead: boolean;
}

/**
 * Compute strip layout for `n` party members. Returns one SlotLayout per
 * slot. Slot 0 (lead) is asymmetric — 60 px wide AND sits 2 px higher
 * than the bench baseline (`top: -10` after accounting for the 8 px
 * size delta + 2 px lift). Bench slots use 52 px and top: 0.
 *
 * Strip width: `60 + (n-1) * 60` (lead + bench-pitches). Centered in
 * `parentWidth`.
 *
 * Explicit fixed positions (NOT flexbox) are load-bearing for the
 * drag-target math in step 5b — flex auto-center reflows mid-drag and
 * makes slot-from-dx calculations stateful and lossy.
 */
export function computeStripLayout(
  n: number,
  parentWidth: number,
): SlotLayout[] {
  if (n <= 0) return [];

  const benchPitch = ORB_BENCH_SIZE + ORB_GAP;
  const stripWidth = ORB_LEAD_SIZE + Math.max(0, n - 1) * benchPitch;
  const stripLeft = Math.round((parentWidth - stripWidth) / 2);

  const slots: SlotLayout[] = [];
  slots.push({
    left: stripLeft,
    top: -(ORB_LEAD_SIZE - ORB_BENCH_SIZE) - ORB_LEAD_LIFT,
    size: ORB_LEAD_SIZE,
    spriteSize: ORB_SPRITE_LEAD,
    isLead: true,
  });
  for (let i = 1; i < n; i++) {
    slots.push({
      left: stripLeft + ORB_LEAD_SIZE + ORB_GAP + (i - 1) * benchPitch,
      top: 0,
      size: ORB_BENCH_SIZE,
      spriteSize: ORB_SPRITE_BENCH,
      isLead: false,
    });
  }
  return slots;
}

/** Member HP as an integer percent (0-100). Defensive: 0 when max ≤ 0. */
export function hpPct(member: PartyMember): number {
  if (member.hp.max <= 0) return 0;
  return Math.round((member.hp.current / member.hp.max) * 100);
}

/** Screen-reader text for one orb. `Pikachu, Lv 12, 35% HP`.
 *  Tombstone state (step 9) will append ", fainted". */
export function ariaLabelFor(member: PartyMember): string {
  const cap = member.species.charAt(0).toUpperCase() + member.species.slice(1);
  return `${cap}, Lv ${member.level}, ${hpPct(member)}% HP`;
}
